import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { useBilling } from '../../contexts/BillingContext';
import { BillingNavigator, DisplayStatus, SalesDocType, SalesDocument } from '../../types/billing';
import { displayStatus, docLabel, docRef, formatBillingDate, formatMoney, fromN, toN, todayLusaka } from '../../utils/billingMoney';
import { exportCsv } from '../../utils/hrExport';
import { Button } from '../../components/ui/Button';
import { DocStatusBadge } from './DocStatusBadge';
type SortKey = 'number' | 'date' | 'due' | 'account' | 'total' | 'balance';
const statusOptions: Record<SalesDocType, DisplayStatus[]> = {
  invoice: ['Draft', 'Unpaid', 'Partially Paid', 'Overdue', 'Paid', 'Credited', 'Void'],
  quotation: ['Draft', 'Sent', 'Accepted', 'Declined', 'Converted'],
  order: ['Open', 'Converted', 'Cancelled']
};

/** Searchable, sortable list of invoices, quotations or orders with contextual actions. */
export function SalesDocumentList({
  type,
  nav
}: {
  type: SalesDocType;
  nav: BillingNavigator;
}) {
  const {
    documents,
    config,
    can,
    convert
  } = useBilling();
  const today = todayLusaka();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'open' | DisplayStatus>(type === 'invoice' ? 'all' : 'all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<{
    key: SortKey;
    dir: 1 | -1;
  }>({
    key: 'date',
    dir: -1
  });
  const [message, setMessage] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const date = (v?: string) => formatBillingDate(v, config.regional.dateFormat);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = documents.filter(d => d.type === type).map(d => ({
      doc: d,
      status: displayStatus(d, today)
    })).filter(({
      doc,
      status: s
    }) => {
      if (status === 'open' && !['Unpaid', 'Partially Paid', 'Overdue'].includes(s)) return false;
      if (status !== 'all' && status !== 'open' && s !== status) return false;
      if (from && doc.date < from) return false;
      if (to && doc.date > to) return false;
      if (!q) return true;
      return [doc.number, doc.accountName, doc.patientName, doc.bmcFileNumber, doc.legacy?.number].some(v => v?.toLowerCase().includes(q));
    });
    const val = (d: SalesDocument): string | number => {
      switch (sort.key) {
        case 'number':
          return d.number ?? '';
        case 'date':
          return d.date + d.createdAt;
        case 'due':
          return d.dueDate ?? d.validUntil ?? '';
        case 'account':
          return d.accountName.toLowerCase();
        case 'total':
          return toN(d.total);
        case 'balance':
          return toN(d.balance);
      }
    };
    return list.sort((a, b) => (val(a.doc) > val(b.doc) ? 1 : val(a.doc) < val(b.doc) ? -1 : 0) * sort.dir);
  }, [documents, type, query, status, from, to, sort, today]);
  const totals = useMemo(() => ({
    total: fromN(rows.reduce((s, r) => s + (r.doc.state === 'Void' ? 0 : toN(r.doc.total)), 0)),
    balance: fromN(rows.reduce((s, r) => s + (r.doc.state === 'Issued' ? toN(r.doc.balance) : 0), 0))
  }), [rows]);
  const header = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => <th className={`px-3 py-2 ${align === 'right' ? 'text-right' : ''}`} aria-sort={sort.key === key ? sort.dir === 1 ? 'ascending' : 'descending' : 'none'} data-id="element-4749" data-mp="qjsavho0l4zi">
      <button type="button" onClick={() => setSort(s => ({
      key,
      dir: s.key === key ? s.dir === 1 ? -1 : 1 : key === 'account' || key === 'number' ? 1 : -1
    }))} className={`inline-flex items-center gap-1 uppercase hover:text-slate-800 ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-4750" data-mp="ljnt7obde47p">
        {label}
        {sort.key === key && (sort.dir === 1 ? <ArrowUpIcon className="h-3 w-3" aria-hidden="true" data-id="element-4751" data-mp="26yl872zxi7s8" /> : <ArrowDownIcon className="h-3 w-3" aria-hidden="true" data-id="element-4752" data-mp="26z38cappp65f" />)}
      </button>
    </th>;
  const doConvert = async (id: string, toType: 'invoice' | 'order') => {
    setBusy(id);
    const result = await convert(id, toType);
    setBusy(null);
    if (!result.ok) return setMessage({
      ok: false,
      text: result.error ?? 'Conversion failed.'
    });
    if (toType === 'invoice' && result.id) nav.editDocument(result.id);else setMessage({
      ok: true,
      text: result.message ?? 'Converted.'
    });
  };
  const label = docLabel(type);
  const plural = type === 'invoice' ? 'Invoices' : type === 'quotation' ? 'Quotations' : 'Orders';
  return <div className="space-y-3" data-id="element-4753" data-mp="1lcufc8lanrhc">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-4754" data-mp="1ldvv2qd9925e">
        <h1 className="text-xl font-bold text-slate-900" data-id="element-4755" data-mp="1qbnue5g8bqjm">{plural}</h1>
        <div className="flex gap-2" data-id="element-4756" data-mp="1q9uleji58u8g">
          <Button size="sm" variant="outline" onClick={() => exportCsv(`${plural}-${today}`, [{
          key: 'number',
          label: 'Number'
        }, {
          key: 'date',
          label: 'Date'
        }, {
          key: 'account',
          label: 'Account'
        }, {
          key: 'patient',
          label: 'Patient'
        }, {
          key: 'status',
          label: 'Status'
        }, {
          key: 'total',
          label: 'Total',
          align: 'right'
        }, {
          key: 'balance',
          label: 'Balance',
          align: 'right'
        }], rows.map(({
          doc,
          status: s
        }) => ({
          number: docRef(doc),
          date: doc.date,
          account: doc.accountName,
          patient: doc.patientName ?? '',
          status: s,
          total: doc.total,
          balance: doc.state === 'Issued' ? doc.balance : 0
        })))} data-id="element-4757" data-mp="1cbmno9da9ow8">Export CSV</Button>
          {can('createDocuments') && <Button size="sm" onClick={() => nav.newDocument(type)} data-id="element-4758" data-mp="1bp8en9gfioah"><PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" data-id="element-4759" data-mp="1bop80ow8bmbr" />New {label.toLowerCase()}</Button>}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3" data-id="element-4760" data-mp="1qfxsykp6sjik">
        <div className="relative min-w-[220px] flex-1" data-id="element-4761" data-mp="1lhe6mj6ol5po">
          <label htmlFor={`${type}-search`} className="sr-only" data-id="element-4762" data-mp="1zfkvj9tnjvd7">Search {plural.toLowerCase()}</label>
          <SearchIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-4763" data-mp="1uh16h0jri3gm" />
          <input id={`${type}-search`} value={query} onChange={e => setQuery(e.target.value)} placeholder="Number, account, patient or file no." className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm" data-id="element-4764" data-mp="s07ed9e86m28" />
        </div>
        <div data-id="element-4765" data-mp="15u8h0e8ast7v">
          <label htmlFor={`${type}-status`} className="block text-[11px] font-medium text-slate-500" data-id="element-4766" data-mp="15tyn4fk4u1ww">Status</label>
          <select id={`${type}-status`} value={status} onChange={e => setStatus(e.target.value as typeof status)} className="mt-0.5 rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" data-id="element-4767" data-mp="1as06z2pdu59f">
            <option value="all" data-id="element-4768" data-mp="1ksanaw6prsst">All</option>
            {type === 'invoice' && <option value="open" data-id="element-4769" data-mp="1ot2wjpfl5xp3">Open (unpaid, partial, overdue)</option>}
            {statusOptions[type].map(s => <option key={s} value={s} data-id="element-4770" data-mp="3miud9wzkk4vn">{s}</option>)}
          </select>
        </div>
        <div data-id="element-4771" data-mp="3qjo3ix0hhvrp"><label htmlFor={`${type}-from`} className="block text-[11px] font-medium text-slate-500" data-id="element-4772" data-mp="3qjo0sp93nho0">From</label><input id={`${type}-from`} type="date" value={from} onChange={e => setFrom(e.target.value)} className="mt-0.5 rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-4773" data-mp="3j054jhauhrxh" /></div>
        <div data-id="element-4774" data-mp="39aliy3vqzjyx"><label htmlFor={`${type}-to`} className="block text-[11px] font-medium text-slate-500" data-id="element-4775" data-mp="39alk477hhq0i">To</label><input id={`${type}-to`} type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-0.5 rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-4776" data-mp="3590biy1zfxw4" /></div>
      </div>

      {message && <p role={message.ok ? 'status' : 'alert'} className={`rounded-md border px-3 py-2 text-sm ${message.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`} data-id="element-4777" data-mp="usbsfnb3kuj">{message.text}</p>}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-id="element-4778" data-mp="3o0axd0supkh5">
        {rows.length === 0 ? <p className="py-14 text-center text-sm text-slate-500" data-id="element-4779" data-mp="2hk78yiquursj">{documents.some(d => d.type === type) ? 'Nothing matches these filters.' : `No ${plural.toLowerCase()} yet.`}</p> : <div className="overflow-x-auto" data-id="element-4780" data-mp="ggeuesin0etk">
            <table className="min-w-full text-sm" data-id="element-4781" data-mp="gjkji7r9ngpa">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold tracking-wide text-slate-500" data-id="element-4782" data-mp="kjkxxsed7rwc">
                <tr data-id="element-4783" data-mp="6kl8wjmxxazn">
                  {header('number', 'Number')}
                  {header('date', 'Date')}
                  {type !== 'order' && header('due', type === 'invoice' ? 'Due' : 'Valid until')}
                  {header('account', 'Account / patient')}
                  {header('total', 'Total', 'right')}
                  {type === 'invoice' && header('balance', 'Balance', 'right')}
                  <th className="px-3 py-2 uppercase" data-id="element-4784" data-mp="phcz7d0wgqd7">Status</th>
                  <th className="px-3 py-2" data-id="element-4785" data-mp="ug7ish6oyiyd"><span className="sr-only" data-id="element-4786" data-mp="ufo89ljdewud">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-4787" data-mp="1i4z54rxlo738">
                {rows.map(({
              doc,
              status: s
            }) => <tr key={doc.id} className="hover:bg-slate-50" data-id="element-4788" data-mp="1mzd3923ncsbl">
                    <td className="whitespace-nowrap px-3 py-2" data-id="element-4789" data-mp="37mnon22sw82j">
                      <button type="button" onClick={() => nav.openDocument(doc.id)} className="font-semibold text-indigo-700 hover:underline" data-id="element-4790" data-mp="32ouexz2agvxi">{docRef(doc)}</button>
                      {doc.legacy && <span className="ml-1.5 text-[10px] text-slate-400" data-id="element-4791" data-mp="3ghtug122mq52">imported</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2" data-id="element-4792" data-mp="2oojp8zcptndx">{date(doc.date)}</td>
                    {type !== 'order' && <td className={`whitespace-nowrap px-3 py-2 ${s === 'Overdue' ? 'font-semibold text-red-700' : ''}`} data-id="element-4793" data-mp="2jq04h29uyhn5">{date(doc.dueDate ?? doc.validUntil)}</td>}
                    <td className="px-3 py-2" data-id="element-4794" data-mp="2xnetw7042pf4">
                      <span className="block font-medium text-slate-900" data-id="element-4795" data-mp="2sq58r3jr8zx3">{doc.accountName}</span>
                      {doc.accountType !== 'Patient' && doc.patientName || doc.bmcFileNumber ? <span className="text-xs text-slate-500" data-id="element-4796" data-mp="807h0ymxi8t7">{doc.accountType !== 'Patient' && doc.patientName ? `${doc.patientName} · ` : ''}{doc.bmcFileNumber}</span> : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums" data-id="element-4797" data-mp="lnjyf5o3nq2i">{fmt(doc.total)}</td>
                    {type === 'invoice' && <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums" data-id="element-4798" data-mp="gvs20ei9iq4g">{doc.state === 'Issued' ? fmt(doc.balance) : '—'}</td>}
                    <td className="px-3 py-2" data-id="element-4799" data-mp="ctf6eix8c8le"><DocStatusBadge status={s} data-id="element-4800" data-mp="7wojpcqx6er0" /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-right" data-id="element-4801" data-mp="7u2brc049x89">
                      <div className="inline-flex gap-1" data-id="element-4802" data-mp="2wahwcfww11f">
                        {doc.state === 'Draft' && (doc.type === 'invoice' || doc.type === 'quotation') || doc.type === 'order' && doc.state === 'Open' ? can('createDocuments') && <Button size="sm" variant="ghost" onClick={() => nav.editDocument(doc.id)} data-id="element-4803" data-mp="1sc06wml62dja">Edit</Button> : null}
                        {doc.type === 'invoice' && doc.state === 'Issued' && toN(doc.balance) > 0 && can('recordPayments') && <Button size="sm" variant="outline" onClick={() => nav.recordPayment({
                    accountId: doc.accountId,
                    invoiceId: doc.id
                  })} data-id="element-4804" data-mp="365xjyq92zs8q">Pay</Button>}
                        {doc.type !== 'invoice' && !['Converted', 'Declined', 'Cancelled'].includes(doc.state) && can('createDocuments') && <Button size="sm" variant="outline" isLoading={busy === doc.id} onClick={() => doConvert(doc.id, 'invoice')} data-id="element-4805" data-mp="2n95uu06uyiwr">To invoice</Button>}
                        <Button size="sm" variant="ghost" onClick={() => nav.openDocument(doc.id)} data-id="element-4806" data-mp="2sad27y539g2w">View</Button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="border-t-2 border-slate-200 bg-slate-50 text-sm font-semibold" data-id="element-4807" data-mp="bjvbtggqvult">
                <tr data-id="element-4808" data-mp="gig1ilydevw0">
                  <td className="px-3 py-2" colSpan={type === 'order' ? 3 : 4} data-id="element-4809" data-mp="ghwui0a9drwr">{rows.length} {rows.length === 1 ? label.toLowerCase() : plural.toLowerCase()}</td>
                  <td className="px-3 py-2 text-right tabular-nums" data-id="element-4810" data-mp="2cnp3h2dvx9fa">{fmt(totals.total)}</td>
                  {type === 'invoice' && <td className="px-3 py-2 text-right tabular-nums" data-id="element-4811" data-mp="2mjs5m88ql828">{fmt(totals.balance)}</td>}
                  <td colSpan={2} data-id="element-4812" data-mp="2hl75s9xecqeb" />
                </tr>
              </tfoot>
            </table>
          </div>}
      </div>
    </div>;
}